import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Download,
  FileText,
  Calendar,
  Filter,
  Search,
  Package,
  Users,
  DollarSign,
  Printer,
  Mail,
  Share2,
} from "lucide-react";

interface Order {
  id: string;
  customerName: string;
  customerEmail: string;
  designName: string;
  status: "pending" | "processing" | "completed" | "shipped" | "delivered";
  amount: number;
  date: string;
  className?: string;
  templateUsed: string;
  notes?: string;
}

interface OrderPDFGeneratorProps {
  orders: Order[];
}

export const OrderPDFGenerator = ({ orders }: OrderPDFGeneratorProps) => {
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [pdfType, setPdfType] = useState<"summary" | "detailed" | "class">(
    "summary"
  );

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.designName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || order.status === statusFilter;
    const matchesDateRange =
      (!dateRange.start || order.date >= dateRange.start) &&
      (!dateRange.end || order.date <= dateRange.end);
    return matchesSearch && matchesStatus && matchesDateRange;
  });

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const selectAllOrders = () => {
    setSelectedOrders(filteredOrders.map((order) => order.id));
  };

  const clearSelection = () => {
    setSelectedOrders([]);
  };

  const generatePDF = (type: "summary" | "detailed" | "class") => {
    const ordersToExport =
      selectedOrders.length > 0
        ? filteredOrders.filter((order) => selectedOrders.includes(order.id))
        : filteredOrders;

    // Mock PDF generation - in real app, this would call an API
    console.log(
      `Generating ${type} PDF for ${ordersToExport.length} orders:`,
      ordersToExport
    );

    // Simulate PDF download
    const blob = new Blob([`PDF content for ${ordersToExport.length} orders`], {
      type: "application/pdf",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders-${type}-${new Date().toISOString().split("T")[0]}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "shipped":
        return "bg-purple-100 text-purple-800";
      case "delivered":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTotalRevenue = () => {
    return filteredOrders.reduce((sum, order) => sum + order.amount, 0);
  };

  const getOrdersByClass = () => {
    const classGroups: { [key: string]: Order[] } = {};
    filteredOrders.forEach((order) => {
      const className = order.className || "Individual Orders";
      if (!classGroups[className]) {
        classGroups[className] = [];
      }
      classGroups[className].push(order);
    });
    return classGroups;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generate Order Reports
          </CardTitle>
          <CardDescription>
            Create PDF reports of your orders for printing, sharing, or record
            keeping
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            value={pdfType}
            onValueChange={(value) =>
              setPdfType(value as "summary" | "detailed" | "class")
            }
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="summary">Summary Report</TabsTrigger>
              <TabsTrigger value="detailed">Detailed Report</TabsTrigger>
              <TabsTrigger value="class">Class Report</TabsTrigger>
            </TabsList>

            {/* Summary Report */}
            <TabsContent value="summary" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {filteredOrders.length}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Total Orders
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    ₵{getTotalRevenue()}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Total Revenue
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {
                      filteredOrders.filter((o) => o.status === "completed")
                        .length
                    }
                  </div>
                  <div className="text-sm text-muted-foreground">Completed</div>
                </div>
              </div>
              <Button onClick={() => generatePDF("summary")} className="w-full">
                <Download className="h-4 w-4 mr-2" />
                Generate Summary PDF
              </Button>
            </TabsContent>

            {/* Detailed Report */}
            <TabsContent value="detailed" className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Detailed report includes customer information, design details,
                and order status for each order.
              </div>
              <Button
                onClick={() => generatePDF("detailed")}
                className="w-full"
              >
                <FileText className="h-4 w-4 mr-2" />
                Generate Detailed PDF
              </Button>
            </TabsContent>

            {/* Class Report */}
            <TabsContent value="class" className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Class report groups orders by class and shows completion status
                for each class.
              </div>
              <div className="space-y-2">
                {Object.entries(getOrdersByClass()).map(
                  ([className, classOrders]) => (
                    <div
                      key={className}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div>
                        <h4 className="font-medium">{className}</h4>
                        <p className="text-sm text-muted-foreground">
                          {classOrders.length} orders • ₵
                          {classOrders.reduce((sum, o) => sum + o.amount, 0)}{" "}
                          total
                        </p>
                      </div>
                      <Badge variant="secondary">
                        {
                          classOrders.filter((o) => o.status === "completed")
                            .length
                        }
                        /{classOrders.length} completed
                      </Badge>
                    </div>
                  )
                )}
              </div>
              <Button onClick={() => generatePDF("class")} className="w-full">
                <Users className="h-4 w-4 mr-2" />
                Generate Class PDF
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Filters and Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Orders</CardTitle>
          <CardDescription>
            Select specific orders or apply filters before generating your PDF
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Search Orders</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by customer, design, or order ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status-filter">Status Filter</Label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Date Range</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, start: e.target.value }))
                  }
                  className="text-sm"
                />
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, end: e.target.value }))
                  }
                  className="text-sm"
                />
              </div>
            </div>
          </div>

          {/* Selection Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                {selectedOrders.length} of {filteredOrders.length} orders
                selected
              </span>
              <Button variant="outline" size="sm" onClick={selectAllOrders}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={clearSelection}>
                Clear Selection
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Mail className="h-4 w-4 mr-2" />
                Email PDF
              </Button>
              <Button variant="outline" size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      <Card>
        <CardHeader>
          <CardTitle>Orders ({filteredOrders.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {filteredOrders.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.id)}
                    onChange={() => toggleOrderSelection(order.id)}
                    aria-label={`Select order ${order.id}`}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{order.designName}</span>
                      <Badge className={getStatusColor(order.status)}>
                        {order.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {order.customerName} • {order.date} • ₵{order.amount}
                    </p>
                    {order.className && (
                      <p className="text-xs text-muted-foreground">
                        Class: {order.className}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm">
                    <Package className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
